import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, FileText, Image as ImageIcon, Sparkles, Bot, ScanLine } from "lucide-react";

const STATUSES = [
  "new", "reviewing", "more_info_required", "suitable", "not_suitable",
  "proposal_in_progress", "proposal_sent", "won", "lost", "on_hold",
];

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [notes, setNotes] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try {
      const [a, n, p] = await Promise.all([
        api.get(`/api/admin/applications/${id}`),
        api.get(`/api/admin/applications/${id}/notes`),
        api.get(`/api/admin/proposals`, { params: { application_id: id } }),
      ]);
      setApp(a.data);
      setNotes(n.data);
      setProposals(p.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  async function patch(update) {
    setSaving(true);
    try {
      const { data } = await api.patch(`/api/admin/applications/${id}`, update);
      setApp(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function addNote() {
    if (!note.trim()) return;
    try {
      await api.post(`/api/admin/applications/${id}/notes`, { note });
      setNote("");
      const n = await api.get(`/api/admin/applications/${id}/notes`);
      setNotes(n.data);
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  const scoreFactors = useMemo(() => {
    if (!app) return [];
    const d = app.data || {};
    return [
      ["Electricity spend", d.average_monthly_bill || "—"],
      ["Annual kWh", d.annual_kwh || "—"],
      ["Ownership", d.ownership || "—"],
      ["Lease expiry", d.lease_expiry || "—"],
      ["Roof access", d.roof_access || "—"],
      ["Connection", d.connection_type || "—"],
      ["After 5pm", d.operates_after_5pm || "—"],
      ["Overnight", d.operates_overnight || "—"],
      ["Uploaded bills", (app.files || []).length ? "Yes" : "No"],
      ["Industry", d.industry || "—"],
      ["Long-term ok", d.long_term_ok || "—"],
    ];
  }, [app]);

  if (!app) return <div className="text-neutral-500" data-testid="app-loading">Loading application…</div>;

  const d = app.data || {};

  return (
    <div data-testid="admin-app-detail">
      <Link to="/admin" className="inline-flex items-center gap-2 text-forest text-[13.5px] font-semibold mb-4">
        <ArrowLeft size={14}/> Back to dashboard
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-6 mb-6">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-2">Application {app.application_number}</p>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-forest leading-tight">
            {d.legal_business_name || "—"}
          </h1>
          <p className="mt-1 text-neutral-500">{d.trading_name}</p>
        </div>
        <div className="rounded-2xl bg-white border border-black/5 p-4 flex items-center gap-3">
          <Sparkles size={18} className="text-bright"/>
          <div>
            <p className="text-[12px] uppercase tracking-widest text-neutral-500 font-semibold">Lead score</p>
            <p className="font-heading text-xl font-semibold text-forest">{app.lead_fit} · {app.lead_score}</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* LEFT — full data */}
        <div className="space-y-6">
          <Panel title="Contact">
            <KV label="Name" value={`${d.first_name || ""} ${d.last_name || ""}`.trim() || "—"} />
            <KV label="Role" value={d.position} />
            <KV label="Email" value={d.email} />
            <KV label="Mobile" value={d.mobile} />
            <KV label="Office phone" value={d.office_phone} />
            <KV label="Preferred contact" value={d.preferred_contact} />
            <KV label="Best time" value={d.best_time_to_contact} />
          </Panel>

          <Panel title="Business">
            <KV label="Legal name" value={d.legal_business_name} />
            <KV label="Trading name" value={d.trading_name} />
            <KV label="ABN" value={d.abn} />
            <KV label="ACN" value={d.acn} />
            <KV label="Website" value={d.business_website} />
            <KV label="Industry" value={(d.industry || "").replace(/_/g," ")} />
            <KV label="Employees" value={d.employees} />
            <KV label="Years operating" value={d.years_in_operation} />
            <KV label="Trading" value={d.currently_trading} />
            <KV label="Profitable" value={d.profitable} />
            <KV label="Business type" value={d.business_type} />
            <KV label="Turnover" value={(d.annual_turnover || "").replace(/_/g," ")} />
          </Panel>

          <Panel title="Site">
            <KV label="Address" value={`${d.site_address || ""} ${d.site_suburb || ""} ${d.site_state || ""} ${d.site_postcode || ""}`} />
            <KV label="Ownership" value={d.ownership} />
            <KV label="Lease expiry" value={d.lease_expiry} />
            <KV label="Landlord approval" value={d.landlord_approval} />
            <KV label="Building type" value={d.building_type} />
            <KV label="Roof type" value={d.roof_type} />
            <KV label="Roof age" value={d.roof_age} />
            <KV label="Roof issues" value={d.roof_issues} />
            <KV label="Usable roof (m²)" value={d.usable_roof_area} />
            <KV label="Storeys" value={d.storeys} />
            <KV label="Roof access" value={d.roof_access} />
            <KV label="Battery space" value={d.battery_space} />
            <KV label="Switchboard space" value={d.switchboard_space} />
          </Panel>

          <Panel title="Electricity usage">
            <KV label="Retailer" value={d.current_retailer} />
            <KV label="NMI" value={d.nmi} />
            <KV label="Meter type" value={d.meter_type} />
            <KV label="Connection" value={d.connection_type} />
            <KV label="Avg monthly bill" value={d.average_monthly_bill} />
            <KV label="Annual kWh" value={d.annual_kwh} />
            <KV label="Highest bill" value={d.highest_monthly_bill} />
            <KV label="Demand charges" value={d.demand_charges} />
            <KV label="Operating hours" value={d.operating_hours} />
            <KV label="Days" value={(d.days_of_operation || []).join(", ")} />
            <KV label="After 5pm" value={d.operates_after_5pm} />
            <KV label="Overnight" value={d.operates_overnight} />
            <KV label="Large loads" value={(d.large_loads || []).join(", ")} />
            <KV label="Other loads" value={d.large_loads_other} />
          </Panel>

          <Panel title="Goals">
            <KV label="Interest" value={d.interest} />
            <KV label="Main goals" value={(d.main_goals || []).join(", ")} />
            <KV label="Backup power" value={d.backup_power} />
            <KV label="Preferred term" value={d.preferred_term} />
            <KV label="Long-term ok" value={d.long_term_ok} />
            <KV label="Target rate" value={d.target_rate} />
            <KV label="Upcoming changes" value={(d.upcoming_changes || []).join(", ")} />
          </Panel>

          <Panel title="Uploaded files">
            {(app.files || []).length === 0 && <p className="text-[13.5px] text-neutral-500">No files uploaded.</p>}
            <ul className="grid md:grid-cols-2 gap-2">
              {(app.files || []).map((f) => (
                <li key={f.file_id} className="flex items-center gap-3 bg-mist border border-black/5 rounded-xl p-3">
                  {String(f.content_type).startsWith("image/") ? <ImageIcon size={16} className="text-forest" /> : <FileText size={16} className="text-forest"/>}
                  <div className="min-w-0 flex-1">
                    <p className="text-[13.5px] font-medium truncate">{f.original_filename}</p>
                    <p className="text-[11.5px] text-neutral-500">{(f.size / 1024).toFixed(0)} KB · {f.category}</p>
                  </div>
                  <a
                    href={`${API_BASE}/files/${f.file_id}`}
                    target="_blank" rel="noreferrer"
                    className="text-forest font-semibold text-[13px] inline-flex items-center gap-1"
                    data-testid={`file-open-${f.file_id}`}
                  >
                    Open <Download size={13}/>
                  </a>
                </li>
              ))}
            </ul>
          </Panel>
        </div>

        {/* RIGHT — Admin actions */}
        <div className="space-y-6">
          <Panel title="Status & assignment">
            <label className="block mb-3">
              <span className="text-[12.5px] font-semibold text-forest">Status</span>
              <select
                value={app.status}
                onChange={(e) => patch({ status: e.target.value })}
                data-testid="app-status-select"
                className="mt-1 w-full h-10 rounded-md border border-input px-3 text-[13.5px] bg-white text-forest"
              >
                {STATUSES.map((s) => <option value={s} key={s}>{s.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="block mb-3">
              <span className="text-[12.5px] font-semibold text-forest">Assigned to</span>
              <Input
                defaultValue={app.assigned_to || ""}
                onBlur={(e) => e.target.value !== (app.assigned_to || "") && patch({ assigned_to: e.target.value })}
                data-testid="app-assigned-to"
                placeholder="Team member email or name"
              />
            </label>
            <label className="block mb-3">
              <span className="text-[12.5px] font-semibold text-forest">Follow-up date</span>
              <Input
                type="date"
                defaultValue={app.follow_up_date || ""}
                onBlur={(e) => e.target.value !== (app.follow_up_date || "") && patch({ follow_up_date: e.target.value })}
                data-testid="app-followup"
              />
            </label>
            <label className="block">
              <span className="text-[12.5px] font-semibold text-forest">Suitability</span>
              <select
                value={app.suitability || ""}
                onChange={(e) => patch({ suitability: e.target.value })}
                data-testid="app-suitability"
                className="mt-1 w-full h-10 rounded-md border border-input px-3 text-[13.5px] bg-white text-forest"
              >
                <option value="">—</option>
                <option value="suitable">Suitable</option>
                <option value="not_suitable">Not suitable</option>
                <option value="pending">Pending</option>
              </select>
            </label>
            {saving && <p className="text-[12px] text-neutral-500 mt-2">Saving…</p>}
          </Panel>

          <Panel title="Qualification">
            <NumericField label="Est. system size (kW)" testId="q-system-kw" value={app.estimated_system_kw} onSave={(v) => patch({ estimated_system_kw: v })} />
            <NumericField label="Est. battery (kWh)" testId="q-battery-kwh" value={app.estimated_battery_kwh} onSave={(v) => patch({ estimated_battery_kwh: v })} />
            <NumericField label="Proposed PPA rate (c/kWh)" testId="q-ppa-rate" value={app.proposed_ppa_rate_cents} onSave={(v) => patch({ proposed_ppa_rate_cents: v })} />
            <NumericField label="Proposed term (years)" testId="q-term" value={app.proposed_term_years} onSave={(v) => patch({ proposed_term_years: v })} />
            <NumericField label="Est. annual savings (AUD)" testId="q-savings" value={app.estimated_annual_savings} onSave={(v) => patch({ estimated_annual_savings: v })} />
            <Button
              className="mt-4 w-full rounded-full bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold h-11"
              onClick={() => navigate(`/admin/applications/${id}/proposal`)}
              data-testid="open-proposal-builder"
            >
              Build indicative proposal
            </Button>
            <Button
              variant="outline"
              className="mt-2 w-full rounded-full border-forest/20 text-forest hover:bg-mist font-semibold h-11"
              onClick={() => navigate(`/admin/green-advisor?application_id=${id}`)}
              data-testid="ask-green-advisor"
            >
              <Bot size={15} className="mr-1.5"/> Ask Green Advisor
            </Button>
            <Button
              variant="outline"
              className="mt-2 w-full rounded-full border-forest/20 text-forest hover:bg-mist font-semibold h-11"
              onClick={() => navigate(`/admin/applications/${id}/nmi-analysis`)}
              data-testid="open-nmi-analysis"
            >
              <ScanLine size={15} className="mr-1.5"/> Deep NMI data analysis
            </Button>
          </Panel>

          <Panel title="Lead score factors">
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              {scoreFactors.map(([k, v]) => (
                <li key={k} className="flex items-center justify-between border-b border-black/5 py-1">
                  <span className="text-neutral-500">{k}</span>
                  <span className="font-semibold text-forest text-right truncate max-w-[60%]">{v || "—"}</span>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Proposals">
            {proposals.length === 0 && <p className="text-[13.5px] text-neutral-500">No proposals yet.</p>}
            <ul className="space-y-2">
              {proposals.map((p) => (
                <li key={p.id} className="flex items-center justify-between rounded-xl border border-black/5 bg-mist p-3">
                  <div>
                    <p className="text-[13.5px] font-semibold text-forest">{p.proposal_number}</p>
                    <p className="text-[11.5px] text-neutral-500">{p.proposal_status} · {p.system_size_kw} kW · {p.proposed_ppa_rate_cents}c</p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={`${API_BASE}/admin/proposals/${p.id}/pdf`}
                      target="_blank" rel="noreferrer"
                      className="text-forest font-semibold text-[12.5px]"
                      data-testid={`proposal-pdf-${p.id}`}
                    >
                      PDF
                    </a>
                    <button
                      onClick={() => navigate(`/admin/applications/${id}/proposal?pid=${p.id}`)}
                      className="text-forest font-semibold text-[12.5px]"
                    >
                      Open
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </Panel>

          <Panel title="Internal notes">
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add an internal note..." data-testid="note-input" />
            <Button onClick={addNote} data-testid="note-submit" className="mt-2 rounded-full h-10 bg-forest hover:bg-forest/90 text-white">Add note</Button>
            <ul className="mt-4 space-y-3 max-h-[280px] overflow-auto">
              {notes.map((n) => (
                <li key={n.id} className="rounded-xl bg-mist p-3">
                  <p className="text-[13.5px] text-neutral-700 whitespace-pre-wrap">{n.note}</p>
                  <p className="text-[11px] text-neutral-500 mt-1">{n.author_email} · {(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </li>
              ))}
            </ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded-2xl bg-white border border-black/5 p-5 md:p-6">
      <h3 className="font-heading text-lg font-semibold text-forest mb-3">{title}</h3>
      <div className="grid gap-1.5">{children}</div>
    </section>
  );
}
function KV({ label, value }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-3 py-1.5 border-b border-black/5 last:border-b-0 text-[13.5px]">
      <span className="text-neutral-500">{label}</span>
      <span className="font-medium text-forest break-words">{value || "—"}</span>
    </div>
  );
}
function NumericField({ label, testId, value, onSave }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <label className="block">
      <span className="text-[12.5px] font-semibold text-forest">{label}</span>
      <Input
        type="number"
        step="any"
        value={v}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => {
          const n = v === "" ? null : parseFloat(v);
          if ((value ?? null) !== n) onSave(n);
        }}
        data-testid={testId}
      />
    </label>
  );
}
